import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { ApiService } from '../../../../core/services/api.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { ClientIInfo } from '../../../../core/models/parking.model';
import { AccessUserListDto, SiteOption } from '../../../../core/models/system-settings/access-users.model';
import {
  FreeQuotaActivityEntityTypes,
  SaveUserSiteAccessPayload,
  UserSiteAccess,
  createEmptyUserSiteAccessForm,
  freeQuotaActivityEntityId,
  mapUserSiteAccessList,
} from '../../../../core/models/system-settings/free-quota.model';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { PssConfirmModalComponent, PssConfirmModalVariant } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';

interface BackofficeUserOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-user-site-access',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalAdminActivityHistoryComponent, PssConfirmModalComponent],
  templateUrl: './user-site-access.component.html',
  styleUrl: './user-site-access.component.css',
})
export class UserSiteAccessComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiSystemSettingsService);
  private readonly apiLegacy = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  readonly activityEntityTypes = FreeQuotaActivityEntityTypes;

  items = signal<UserSiteAccess[]>([]);
  clients = signal<ClientIInfo[]>([]);
  backofficeUsers = signal<BackofficeUserOption[]>([]);
  selectedRow = signal<UserSiteAccess | null>(null);
  historyRow = signal<UserSiteAccess | null>(null);
  isLoading = signal(false);
  isSaving = signal(false);
  showActivityHistory = false;
  filterOnlySelectedSite = true;
  userSearch = '';

  showConfirm = false;
  confirmTitle = 'ยืนยันการดำเนินการ?';
  confirmMessage = '';
  confirmHighlight = '';
  confirmMessageSuffix = 'กด ใช่ เพื่อยืนยัน หรือ ไม่ เพื่อยกเลิก';
  confirmVariant: PssConfirmModalVariant = 'warning';
  confirmIcon = 'help';
  confirmLoading = false;
  private pendingConfirmAction: (() => void) | null = null;

  form: SaveUserSiteAccessPayload = createEmptyUserSiteAccessForm(0);

  get sites(): SiteOption[] {
    return this.siteState.sites();
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get historyEntityId(): number {
    return freeQuotaActivityEntityId(this.historyRow()?.id);
  }

  get filteredUsers(): BackofficeUserOption[] {
    const q = this.userSearch.trim().toLowerCase();
    const all = this.backofficeUsers();
    if (!q) return all.slice(0, 80);
    return all.filter((u) => u.label.toLowerCase().includes(q)).slice(0, 80);
  }

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        if (!this.form.id) {
          this.form.siteId = site.id;
          this.loadClients(site.id);
        }
        if (this.filterOnlySelectedSite) {
          this.loadTable();
        }
      });
    });
  }

  ngOnInit(): void {
    const defaultSiteId = this.siteState.selectedSite()?.id ?? 0;
    this.form = createEmptyUserSiteAccessForm(defaultSiteId);
    this.loadBackofficeUsers();
    this.loadTable();
    if (defaultSiteId) {
      this.loadClients(defaultSiteId);
    }
  }

  onRefresh(): void {
    this.loadTable();
  }

  onToggleSiteFilter(): void {
    this.loadTable();
  }

  onFormSiteChange(): void {
    this.form.clientId = null;
    this.loadClients(this.form.siteId);
  }

  startAdd(): void {
    this.resetForm();
    const el = document.getElementById('user-site-access-form');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  selectRow(row: UserSiteAccess): void {
    this.selectedRow.set(row);
    this.form = {
      userId: 0,
      menuId: 0,
      id: row.id,
      resUserId: row.resUserId,
      siteId: row.siteId,
      clientId: row.clientId,
      active: row.active,
      canManageQuota: row.canManageQuota,
      canDeleteCardReturnQuota: row.canDeleteCardReturnQuota,
    };
    this.loadClients(row.siteId);
  }

  resetForm(): void {
    this.selectedRow.set(null);
    const siteId = this.siteState.selectedSite()?.id ?? this.form.siteId ?? 0;
    this.form = createEmptyUserSiteAccessForm(siteId);
    this.form.active = true;
    this.loadClients(siteId);
  }

  openHistory(row: UserSiteAccess, event: Event): void {
    event.stopPropagation();
    if (freeQuotaActivityEntityId(row.id) <= 0) return;
    this.historyRow.set(row);
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
    this.historyRow.set(null);
  }

  onSave(): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    if (!this.form.resUserId) {
      this.toast.error('กรุณาเลือกผู้ใช้ Backoffice');
      return;
    }
    if (!this.form.siteId) {
      this.toast.error('กรุณาเลือกลาน');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload: SaveUserSiteAccessPayload = {
      ...this.form,
      userId,
      menuId,
      id: this.form.id && this.form.id > 0 ? this.form.id : null,
      clientId: this.form.clientId && this.form.clientId > 0 ? this.form.clientId : null,
    };

    const userLabel = this.backofficeUsers().find((u) => u.id === payload.resUserId)?.label
      || `User #${payload.resUserId}`;
    const siteLabel = this.sites.find((s) => s.id === payload.siteId)?.name || `Site #${payload.siteId}`;

    this.askConfirm({
      title: payload.id ? 'ยืนยันแก้ไขสิทธิ์?' : 'ยืนยันเพิ่มสิทธิ์?',
      message: 'จะบันทึกสิทธิ์ผู้ใช้ตามลาน',
      highlight: `${userLabel} · ${siteLabel}`,
      variant: 'warning',
      icon: 'save',
    }, () => {
      this.isSaving.set(true);
      this.confirmLoading = true;
      this.api.saveUserSiteAccess(payload).subscribe({
        next: (res) => {
          if (res?.isSuccess ?? res?.IsSuccess) {
            this.toast.success(res?.message ?? res?.Message ?? 'บันทึกสำเร็จ');
            this.loadTable();
            this.resetForm();
          } else {
            this.toast.error(res?.message ?? res?.Message ?? 'บันทึกไม่สำเร็จ');
          }
          this.isSaving.set(false);
          this.confirmLoading = false;
          this.showConfirm = false;
        },
        error: () => {
          this.toast.error('เกิดข้อผิดพลาดขณะบันทึก');
          this.isSaving.set(false);
          this.confirmLoading = false;
          this.showConfirm = false;
        },
      });
    });
  }

  askConfirm(
    opts: {
      title: string;
      message: string;
      highlight?: string;
      variant?: PssConfirmModalVariant;
      icon?: string;
    },
    action: () => void
  ): void {
    this.confirmTitle = opts.title;
    this.confirmMessage = opts.message;
    this.confirmHighlight = opts.highlight ?? '';
    this.confirmMessageSuffix = 'กด ใช่ เพื่อยืนยัน หรือ ไม่ เพื่อยกเลิก';
    this.confirmVariant = opts.variant ?? 'warning';
    this.confirmIcon = opts.icon ?? 'help';
    this.confirmLoading = false;
    this.pendingConfirmAction = action;
    this.showConfirm = true;
  }

  onConfirmYes(): void {
    const action = this.pendingConfirmAction;
    this.pendingConfirmAction = null;
    action?.();
  }

  onConfirmNo(): void {
    if (this.confirmLoading) return;
    this.showConfirm = false;
    this.pendingConfirmAction = null;
  }

  trackRow(_: number, row: UserSiteAccess): number {
    return row.id;
  }

  private loadTable(): void {
    this.isLoading.set(true);
    const siteId = this.filterOnlySelectedSite ? this.selectedSiteId || null : null;
    this.api.getUserSiteAccess(null, siteId).subscribe({
      next: (res) => {
        this.items.set(mapUserSiteAccessList(res));
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดสิทธิ์ผู้ใช้ล้มเหลว');
      },
    });
  }

  private loadBackofficeUsers(): void {
    this.api.getAccessUsersForTable(1, 300).subscribe({
      next: (res) => {
        const rows = (res?.data ?? []) as AccessUserListDto[];
        this.backofficeUsers.set(
          rows.map((u) => ({
            id: u.id,
            label: `${u.displayName || u.login || 'User'} (#${u.id})`,
          }))
        );
      },
      error: () => this.backofficeUsers.set([]),
    });
  }

  private loadClients(siteId: number): void {
    if (!siteId) {
      this.clients.set([]);
      return;
    }
    this.apiLegacy.GetClientIInfo(siteId).subscribe({
      next: (res) => this.clients.set(res ?? []),
      error: () => this.clients.set([]),
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
